import { motion } from 'framer-motion'
import type { ReactionCount } from '@/types/comment'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/store'
import ReactionPicker from './ReactionPicker'
import TooltipOverlay from '@/components/TooltipOverlay'

interface ReactionBarProps {
  commentId: string
  workspaceId: string
  reactions: ReactionCount[]
}

const ReactionBar = ({ commentId, workspaceId, reactions }: ReactionBarProps) => {
  const { user, toggleReaction } = useAppStore()

  const handleReactionClick = async (reactionType: string) => {
    if (!user) return
    try {
      await toggleReaction(workspaceId, commentId, reactionType, user.userId)
    } catch (error) {
      console.error('Failed to toggle reaction:', error)
    }
  }

  const handleReactionSelect = async (emoji: string) => {
    if (!user) return
    try {
      await toggleReaction(workspaceId, commentId, emoji, user.userId)
    } catch (error) {
      console.error('Failed to add reaction:', error)
    }
  }

  const isUserReacted = (reaction: ReactionCount): boolean => {
    if (!user) return false
    return reaction.user_ids.includes(user.userId)
  }

  const getUserNamesForReaction = (reaction: ReactionCount): string => {
    if (reaction.count === 1) {
      const userId = reaction.user_ids[0]
      return user?.userId === userId ? 'You' : 'Someone'
    }
    if (reaction.count === 2) {
      const hasCurrentUser = reaction.user_ids.includes(user?.userId || '')
      return hasCurrentUser ? 'You and 1 other' : '2 people'
    }
    const hasCurrentUser = reaction.user_ids.includes(user?.userId || '')
    const othersCount = hasCurrentUser ? reaction.count - 1 : reaction.count
    return hasCurrentUser
      ? `You and ${othersCount} others`
      : `${reaction.count} people`
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {/* Existing reactions */}
      {reactions.map((reaction) => {
        const userReacted = isUserReacted(reaction)
        const tooltipText = `${getUserNamesForReaction(reaction)} reacted with ${reaction.reaction_type}`

        return (
          <TooltipOverlay key={reaction.reaction_type} message={tooltipText}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleReactionClick(reaction.reaction_type)}
              className={cn(
                "flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium transition-all border",
                userReacted
                  ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400"
                  : "bg-gray-100 dark:bg-dark-accent border-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-muted"
              )}
            >
              <span className="leading-none">{reaction.reaction_type}</span>
              <span className="text-xs font-semibold leading-none">{reaction.count}</span>
            </motion.button>
          </TooltipOverlay>
        )
      })}

      {/* Add reaction button */}
      <ReactionPicker onReactionSelect={handleReactionSelect} disabled={!user} />
    </div>
  )
}

export default ReactionBar
